import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  FaUser, FaSpinner, FaTimes, FaFileAlt, 
  FaBell, FaEdit, FaEye, FaLock, FaClock, FaMoneyBillWave, 
  FaPiggyBank, FaArrowRight, FaArrowLeft, FaCheck, FaPaperPlane,
  FaBullseye, FaShieldAlt, FaWallet, FaChartPie, FaBullhorn,
  FaGraduationCap, FaRing, FaUmbrellaBeach, FaBuilding, FaPhone, FaGlobe,
  FaEnvelope, FaCertificate, FaIdCard, FaMapMarkerAlt, FaExternalLinkAlt,
  FaUserPlus, FaGift
} from 'react-icons/fa';
import axios from 'axios';
import ContentLibrary from '../components/ContentLibrary';
import { ClientDataForm, ClientFinancialSummary } from '../components/client';
import { AnnouncementCard } from '../components/shared';
import OnboardingWizard from '../components/client/OnboardingWizard';
import ReferFriendModal from '../components/client/ReferFriendModal';
import PendingApprovalBanner from '../components/client/PendingApprovalBanner';
import UpgradeModal from '../components/UpgradeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Goal Card Component
const GoalCard = ({ icon: Icon, title, count, color }) => {
  const colors = {
    blue: 'text-blue-400 bg-blue-500/20',
    pink: 'text-pink-400 bg-pink-500/20',
    green: 'text-green-400 bg-green-500/20',
    purple: 'text-purple-400 bg-purple-500/20'
  };

  return (
    <div className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className={colors[color].split(' ')[0]} />
      </div>
      <p className="text-gray-400 text-sm">{title}</p>
      <p className="text-white font-bold text-xl">{count}</p>
    </div>
  );
};

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFreePlan = user?.plan === 'plus';
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [goals, setGoals] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [advisor, setAdvisor] = useState(null);
  const [showDataForm, setShowDataForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);

  // Form data state with all sections
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', date_of_birth: '', age: '', pan_number: '', address: '', occupation: '', profile_occupation: '',
    spouse_name: '', spouse_dob: '', spouse_occupation: '', total_dependents: 0, children: [],
    annual_income: '', monthly_income: '', other_income: '', rental_income: '', interest_income: '', dividend_income: '',
    monthly_expenses: '', rent_emi: '', utilities: '', education_expenses: '', medical_expenses: '', lifestyle_expenses: '',
    savings_account: '', fixed_deposits: '', mutual_funds: '', stocks: '', ppf_epf: '', real_estate_value: '', gold_jewelry: '', other_assets: '',
    home_loan: '', home_loan_emi: '', home_loan_protected: false, car_loan: '', car_loan_emi: '', car_loan_protected: false,
    personal_loan: '', personal_loan_emi: '', personal_loan_protected: false, education_loan: '', education_loan_emi: '', education_loan_protected: false,
    credit_card_outstanding: '', other_liabilities: '',
    life_insurance_cover: '', life_insurance_premium: '', health_insurance_cover: '', health_insurance_premium: '',
    term_insurance_cover: '', term_insurance_premium: '', vehicle_insurance: '',
    retirement_age: 60, retirement_monthly_expense: '', child_goals: [], other_goals: [],
    home_purchase_goal: '', vacation_fund: '', emergency_fund_target: '',
    risk_tolerance: 'moderate', investment_experience: 'beginner', investment_horizon: '5-10 years',
  });

  const tabs = [
    { id: 0, label: 'Personal Info', icon: FaUser },
    { id: 1, label: 'Income & Expenses', icon: FaMoneyBillWave },
    { id: 2, label: 'Assets', icon: FaPiggyBank },
    { id: 3, label: 'Liabilities', icon: FaShieldAlt },
    { id: 4, label: 'Insurance', icon: FaShieldAlt },
    { id: 5, label: 'Goals', icon: FaBullseye },
  ];

  const getToken = useCallback(() => localStorage.getItem('token'), []);
  const headers = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), [getToken]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const hdrs = headers();
      const [profileRes, summaryRes, goalsRes, notifRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/client/my-profile`, { headers: hdrs }),
        axios.get(`${API}/client/my-financial-summary`, { headers: hdrs }).catch(() => ({ data: { has_data: false } })),
        axios.get(`${API}/client/my-goals`, { headers: hdrs }).catch(() => ({ data: {} })),
        axios.get(`${API}/notifications`, { headers: hdrs }).catch(() => ({ data: { notifications: [] } })),
        axios.get(`${API}/client/announcements`, { headers: hdrs }).catch(() => ({ data: [] }))
      ]);
      setProfileData(profileRes.data);
      setFinancialSummary(summaryRes.data);
      setGoals(goalsRes.data);
      setNotifications(notifRes.data.notifications || []);
      setAnnouncements(announcementsRes.data || []);
      setAdvisor(profileRes.data.advisor);
      
      // Check if user needs onboarding (no profile data and hasn't completed onboarding)
      const hasCompletedOnboarding = localStorage.getItem(`onboarding_completed_${user?.id}`);
      if (!profileRes.data.has_profile && !hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
      
      if (profileRes.data.profile) {
        const p = profileRes.data.profile;
        setFormData(prev => ({ ...prev, ...p, name: p.name || user?.name || '', email: p.email || user?.email || '' }));
      } else {
        setFormData(prev => ({ ...prev, name: user?.name || '', email: user?.email || '' }));
      }
    } catch (error) { /* silenced */ }
    finally { setLoading(false); }
  }, [headers, user?.id, user?.name, user?.email]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  const handleOnboardingComplete = () => {
    localStorage.setItem(`onboarding_completed_${user?.id}`, 'true');
    setShowOnboarding(false);
    setShowDataForm(true); // Open the data form after onboarding
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem(`onboarding_completed_${user?.id}`, 'true');
    setShowOnboarding(false);
  };

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleAddChild = () => setFormData(prev => ({ ...prev, children: [...prev.children, { name: '', age: '', education_goal: '', marriage_goal: '' }] }));
  const handleRemoveChild = (index) => setFormData(prev => ({ ...prev, children: prev.children.filter((_, i) => i !== index) }));
  const handleChildChange = (index, field, value) => setFormData(prev => ({ ...prev, children: prev.children.map((child, i) => i === index ? { ...child, [field]: value } : child) }));
  const handleAddChildGoal = () => setFormData(prev => ({ ...prev, child_goals: [...(prev.child_goals || []), { child_name: '', goal_type: 'education', target_amount: '', target_year: '', current_savings: '', notes: '' }] }));
  const handleRemoveChildGoal = (index) => setFormData(prev => ({ ...prev, child_goals: prev.child_goals.filter((_, i) => i !== index) }));
  const handleChildGoalChange = (index, field, value) => setFormData(prev => ({ ...prev, child_goals: prev.child_goals.map((goal, i) => i === index ? { ...goal, [field]: value } : goal) }));
  const handleAddOtherGoal = () => setFormData(prev => ({ ...prev, other_goals: [...(prev.other_goals || []), { goal_name: '', target_amount: '', target_year: '', current_savings: '', priority: 'medium', notes: '' }] }));
  const handleRemoveOtherGoal = (index) => setFormData(prev => ({ ...prev, other_goals: prev.other_goals.filter((_, i) => i !== index) }));
  const handleOtherGoalChange = (index, field, value) => setFormData(prev => ({ ...prev, other_goals: prev.other_goals.map((goal, i) => i === index ? { ...goal, [field]: value } : goal) }));

  const handleSaveProfile = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/save-profile`, formData, { headers: headers() });
      setMessage({ type: 'success', text: 'Profile saved successfully!' });
      fetchAllData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save profile' }); }
    finally { setSubmitting(false); }
  };

  const handleSubmitProfile = async () => {
    if (!window.confirm('Are you sure you want to submit your profile for advisor review? You can still edit it later.')) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/save-profile`, formData, { headers: headers() });
      await axios.post(`${API}/client/submit-profile`, {}, { headers: headers() });
      setMessage({ type: 'success', text: 'Profile submitted successfully! Your advisor will review it shortly.' });
      setShowDataForm(false);
      fetchAllData();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to submit profile' }); }
    finally { setSubmitting(false); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

  const getStatusBadge = (status) => {
    const badges = {
      draft: <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs flex items-center"><FaEdit className="mr-1" /> Draft</span>,
      submitted: <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs flex items-center"><FaClock className="mr-1" /> Under Review</span>,
      report_access_granted: <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs flex items-center"><FaEye className="mr-1" /> Report Available</span>,
      download_allowed: <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs flex items-center"><FaFileAlt className="mr-1" /> Download Ready</span>
    };
    return badges[status] || <span className="px-3 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">Unknown</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-96" data-testid="client-dashboard-loading"><FaSpinner className="animate-spin text-[#FFD700] text-4xl" /></div>;

  // Check if client is pending approval
  if (user?.status === 'pending_approval') {
    return <PendingApprovalBanner userName={user?.name} />;
  }

  const hasProfile = profileData?.has_profile;
  const canViewReport = profileData?.can_view_report;
  const profileStatus = profileData?.profile?.profile_status || 'draft';

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard
          user={user}
          advisor={advisor}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border ${message.type === 'success' ? 'bg-green-500/20 border-green-500 text-green-400' : 'bg-red-500/20 border-red-500 text-red-400'}`}>
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right hover:opacity-70"><FaTimes /></button>
        </div>
      )}

      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl">
        {/* Refer a Friend Banner - Top Most Visible */}
        <div 
          onClick={() => setShowReferModal(true)}
          className="mb-6 p-4 bg-gradient-to-r from-green-500/20 via-emerald-500/20 to-green-500/20 rounded-xl border-2 border-green-500/50 cursor-pointer hover:border-green-400 hover:shadow-lg hover:shadow-green-500/20 transition-all group"
          data-testid="refer-friend-banner"
        >
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <FaUserPlus size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg flex items-center">
                  <FaGift className="text-[#FFD700] mr-2" />
                  Refer a Friend & Earn ₹300!
                </h3>
                <p className="text-gray-300 text-sm">Help your friends achieve financial clarity. Click here to refer now!</p>
              </div>
            </div>
            <button 
              className="px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0A1929] font-bold rounded-xl hover:shadow-lg hover:shadow-[#FFD700]/30 transition-all flex items-center space-x-2 whitespace-nowrap"
            >
              <FaUserPlus />
              <span>Refer Now</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">Welcome, <span className="text-[#FFD700]">{user?.name || 'Client'}!</span></h1>
            <p className="text-gray-300 mb-4">Your Personal Financial Planning Dashboard</p>
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm">Status:</span>
              {getStatusBadge(profileStatus)}
            </div>
            {/* Free Account Validity */}
            {isFreePlan && user?.free_account_valid_until && (
              <div className="mt-3 flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-4 py-2" data-testid="free-validity-banner">
                <FaClock className="text-cyan-400 flex-shrink-0" size={14} />
                <p className="text-cyan-300 text-sm">
                  Free account valid until: <strong className="text-white">{new Date(user.free_account_valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                </p>
              </div>
            )}
          </div>
          {advisor && (
            <div className="mt-4 lg:mt-0 lg:ml-8 bg-[#0F172A]/50 rounded-xl p-5 border border-[#2D5A8F] min-w-[300px]" data-testid="advisor-info">
              <p className="text-gray-400 text-xs mb-3 uppercase tracking-wider">Your Financial Advisor</p>
              
              {/* Advisor Photo and Name */}
              <div className="flex items-center space-x-4 mb-4">
                {advisor.branding?.profile_photo ? (
                  <img 
                    src={advisor.branding.profile_photo} 
                    alt={advisor.name} 
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#FFD700]"
                    data-testid="advisor-photo"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#1E3A5F] flex items-center justify-center border-2 border-[#2D5A8F]">
                    <FaUser className="text-gray-400 text-2xl" />
                  </div>
                )}
                <div>
                  <p className="text-white font-bold text-lg">{advisor.branding?.name || advisor.name}</p>
                  {advisor.branding?.designation && (
                    <p className="text-[#FFD700] text-sm">{advisor.branding.designation}</p>
                  )}
                  {advisor.branding?.city && (
                    <p className="text-gray-400 text-xs flex items-center mt-1">
                      <FaMapMarkerAlt className="mr-1" /> {advisor.branding.city}
                    </p>
                  )}
                </div>
              </div>

              {/* Company Info with Logo */}
              {advisor.branding?.company && (
                <div className="flex items-center space-x-3 mb-3 pb-3 border-b border-[#2D5A8F]">
                  {advisor.branding?.company_logo ? (
                    <img 
                      src={advisor.branding.company_logo} 
                      alt={advisor.branding.company} 
                      className="w-10 h-10 object-contain rounded"
                      data-testid="company-logo"
                    />
                  ) : (
                    <FaBuilding className="text-[#FFD700] text-lg" />
                  )}
                  <p className="text-gray-300 text-sm">{advisor.branding.company}</p>
                </div>
              )}

              {/* Certifications */}
              {(advisor.branding?.certifications || advisor.branding?.irda_licence || advisor.branding?.amfi_licence || advisor.branding?.ira_number || advisor.branding?.other_certification) && (
                <div className="mb-3 pb-3 border-b border-[#2D5A8F]">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-2 flex items-center">
                    <FaCertificate className="mr-1 text-[#FFD700]" /> Certifications
                  </p>
                  <div className="space-y-1">
                    {advisor.branding?.certifications && (
                      <p className="text-gray-300 text-sm">{advisor.branding.certifications}</p>
                    )}
                    {advisor.branding?.irda_licence && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">IRDA:</span>&nbsp;{advisor.branding.irda_licence}
                      </p>
                    )}
                    {advisor.branding?.amfi_licence && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">AMFI:</span>&nbsp;{advisor.branding.amfi_licence}
                      </p>
                    )}
                    {advisor.branding?.ira_number && (
                      <p className="text-gray-400 text-xs flex items-center">
                        <FaIdCard className="mr-2 text-[#FFD700]" />
                        <span className="text-gray-500">IRA:</span>&nbsp;{advisor.branding.ira_number}
                      </p>
                    )}
                    {advisor.branding?.other_certification && (
                      <p className="text-gray-400 text-xs">{advisor.branding.other_certification}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Details */}
              <div className="space-y-2">
                {(advisor.branding?.contact || advisor.phone) && (
                  <a 
                    href={`tel:${advisor.branding?.contact || advisor.phone}`}
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors"
                    data-testid="advisor-phone"
                  >
                    <FaPhone className="mr-2 text-[#FFD700]" />
                    {advisor.branding?.contact || advisor.phone}
                  </a>
                )}
                {(advisor.branding?.email || advisor.email) && (
                  <a 
                    href={`mailto:${advisor.branding?.email || advisor.email}`}
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors"
                    data-testid="advisor-email"
                  >
                    <FaEnvelope className="mr-2 text-[#FFD700]" />
                    {advisor.branding?.email || advisor.email}
                  </a>
                )}
                {advisor.branding?.website && (
                  <a 
                    href={advisor.branding.website.startsWith('http') ? advisor.branding.website : `https://${advisor.branding.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-gray-300 text-sm flex items-center hover:text-[#FFD700] transition-colors group"
                    data-testid="advisor-website"
                  >
                    <FaGlobe className="mr-2 text-[#FFD700]" />
                    <span className="group-hover:underline">{advisor.branding.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                    <FaExternalLinkAlt className="ml-2 text-xs text-gray-500" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-4 border border-[#2D5A8F]" data-testid="notifications-section">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center"><FaBell className="mr-2 text-[#FFD700]" /> Notifications</h3>
          <div className="space-y-2">
            {notifications.slice(0, 3).map((notif) => (
              <div key={notif.id} className={`p-3 rounded-lg border ${notif.notification_type === 'success' ? 'bg-green-500/10 border-green-500/50' : notif.notification_type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-blue-500/10 border-blue-500/50'}`}>
                <p className="text-white font-medium">{notif.title}</p>
                <p className="text-gray-300 text-sm">{notif.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      {!hasProfile || showDataForm ? (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]" data-testid="data-entry-form">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#FFD700] flex items-center"><FaFileAlt className="mr-2" /> Arth Gurukul - Complete Financial Profile</h2>
            {hasProfile && <button onClick={() => setShowDataForm(false)} className="text-gray-400 hover:text-white" data-testid="close-form-btn"><FaTimes size={20} /></button>}
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-[#2D5A8F] pb-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isLocked = isFreePlan && tab.id > 0;
              return (
                <button 
                  key={tab.id} 
                  onClick={() => {
                    if (isLocked) {
                      setShowUpgradeModal(true);
                    } else {
                      setActiveTab(tab.id);
                    }
                  }} 
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                    isLocked 
                      ? 'bg-[#0F172A]/50 text-gray-600 hover:text-gray-500 cursor-pointer'
                      : activeTab === tab.id 
                        ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929]' 
                        : 'bg-[#0F172A] text-gray-400 hover:text-white hover:bg-[#1E3A5F]'
                  }`} 
                  data-testid={`tab-${tab.id}`}
                >
                  {isLocked ? <FaLock size={14} className="text-gray-600" /> : <Icon size={16} />}
                  <span className="hidden sm:inline">{tab.label}</span>
                  {isLocked && <span className="text-[10px] text-gray-600 hidden md:inline">PRO+</span>}
                </button>
              );
            })}
          </div>

          {/* Form Content */}
          <div className="min-h-[400px]">
            <ClientDataForm
              formData={formData}
              activeTab={activeTab}
              handleInputChange={handleInputChange}
              handleAddChild={handleAddChild}
              handleRemoveChild={handleRemoveChild}
              handleChildChange={handleChildChange}
              handleAddChildGoal={handleAddChildGoal}
              handleRemoveChildGoal={handleRemoveChildGoal}
              handleChildGoalChange={handleChildGoalChange}
              handleAddOtherGoal={handleAddOtherGoal}
              handleRemoveOtherGoal={handleRemoveOtherGoal}
              handleOtherGoalChange={handleOtherGoalChange}
              formatCurrency={formatCurrency}
            />
          </div>

          {/* Navigation & Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-[#2D5A8F]">
            <button onClick={() => setActiveTab(Math.max(0, activeTab - 1))} disabled={activeTab === 0} className="flex-1 py-3 bg-[#1E3A5F] hover:bg-[#2D5A8F] text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2 border border-[#2D5A8F] disabled:opacity-50 disabled:cursor-not-allowed" data-testid="prev-tab-btn"><FaArrowLeft /><span>Previous</span></button>
            <button onClick={handleSaveProfile} disabled={submitting} className="flex-1 py-3 bg-[#1E3A5F] hover:bg-[#2D5A8F] text-white font-medium rounded-lg transition-all flex items-center justify-center space-x-2 border border-[#2D5A8F]" data-testid="save-draft-btn">{submitting ? <FaSpinner className="animate-spin" /> : <FaCheck />}<span>{isFreePlan && activeTab === 0 ? 'Save Personal Info' : 'Save Draft'}</span></button>
            {activeTab < tabs.length - 1 ? (
              isFreePlan && activeTab === 0 ? (
                <button onClick={() => setShowUpgradeModal(true)} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg transition-all flex items-center justify-center space-x-2" data-testid="next-tab-btn"><FaLock size={14} /><span>Upgrade to Continue</span></button>
              ) : (
                <button onClick={() => setActiveTab(activeTab + 1)} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg transition-all flex items-center justify-center space-x-2" data-testid="next-tab-btn"><span>Next</span><FaArrowRight /></button>
              )
            ) : (
              <button onClick={handleSubmitProfile} disabled={submitting || !formData.name || !formData.email || !formData.phone} className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold rounded-lg transition-all flex items-center justify-center space-x-2 disabled:opacity-50" data-testid="submit-profile-btn">{submitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}<span>Submit for Review</span></button>
            )}
          </div>
          <p className="text-gray-400 text-sm mt-4 text-center">* After submission, your financial advisor will review your details and grant you access to view your detailed report.</p>
        </div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="quick-actions">
            <button onClick={() => setShowDataForm(true)} className="p-4 bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] hover:border-[#FFD700] transition-all text-left" data-testid="edit-details-btn">
              <FaEdit className="text-[#FFD700] text-2xl mb-2" /><p className="text-white font-bold">Edit My Details</p><p className="text-gray-400 text-sm">Update your financial information</p>
            </button>
            {canViewReport ? (
              <button onClick={() => navigate(`/dashboard/clients/${profileData?.profile?.id}/view`)} className="p-4 bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-green-500 hover:border-[#FFD700] transition-all text-left" data-testid="view-report-btn">
                <FaEye className="text-green-400 text-2xl mb-2" /><p className="text-white font-bold">View My Report</p><p className="text-gray-400 text-sm">See your detailed financial analysis</p>
              </button>
            ) : (
              <div className="p-4 bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] opacity-60" data-testid="view-report-locked">
                <FaLock className="text-gray-500 text-2xl mb-2" /><p className="text-gray-400 font-bold">View Report</p><p className="text-gray-500 text-sm">Awaiting advisor approval</p>
              </div>
            )}
            {financialSummary?.has_finalized_report ? (
              <button onClick={() => navigate(`/dashboard/clients/${profileData?.profile?.id}/view`)} className="p-4 bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#FFD700] hover:border-[#D4AF37] transition-all text-left" data-testid="final-report-btn">
                <FaFileAlt className="text-[#FFD700] text-2xl mb-2" /><p className="text-white font-bold">Final Report</p><p className="text-gray-400 text-sm">View your complete financial plan</p>
              </button>
            ) : (
              <div className="p-4 bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] opacity-60" data-testid="final-report-locked">
                <FaLock className="text-gray-500 text-2xl mb-2" /><p className="text-gray-400 font-bold">Final Report</p><p className="text-gray-500 text-sm">Pending advisor finalization</p>
              </div>
            )}
          </div>

          {/* Financial Summary */}
          <ClientFinancialSummary financialSummary={financialSummary} formatCurrency={formatCurrency} />

          {/* Basic Financial Summary - Shows when no finalized report */}
          {financialSummary?.has_data && !financialSummary?.has_finalized_report && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="basic-financial-summary">
              <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
                <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm">Net Worth</p><p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(financialSummary.net_worth)}</p></div><FaWallet className="text-[#FFD700] text-3xl opacity-50" /></div>
              </div>
              <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
                <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm">Total Investments</p><p className="text-2xl font-bold text-green-400">{formatCurrency(financialSummary.total_investments)}</p></div><FaChartPie className="text-green-400 text-3xl opacity-50" /></div>
              </div>
              <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
                <div className="flex items-center justify-between"><div><p className="text-gray-400 text-sm">Total Liabilities</p><p className="text-2xl font-bold text-red-400">{formatCurrency(financialSummary.total_liabilities)}</p></div><FaShieldAlt className="text-red-400 text-3xl opacity-50" /></div>
              </div>
            </div>
          )}

          {/* Goals Overview */}
          {goals && (goals.education_goals?.length > 0 || goals.marriage_goals?.length > 0 || goals.retirement_goals?.length > 0 || goals.custom_goals?.length > 0) && (
            <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]" data-testid="goals-overview">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center"><FaBullseye className="mr-2 text-[#FFD700]" /> My Financial Goals</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <GoalCard icon={FaGraduationCap} title="Education Goals" count={goals.education_goals?.length || 0} color="blue" />
                <GoalCard icon={FaRing} title="Marriage Goals" count={goals.marriage_goals?.length || 0} color="pink" />
                <GoalCard icon={FaUmbrellaBeach} title="Retirement Goals" count={goals.retirement_goals?.length || 0} color="green" />
                <GoalCard icon={FaBullseye} title="Custom Goals" count={goals.custom_goals?.length || 0} color="purple" />
              </div>
            </div>
          )}

          {/* Status Message for Pending Review */}
          {profileStatus === 'submitted' && (
            <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-xl p-6" data-testid="pending-review-message">
              <div className="flex items-center space-x-4">
                <FaClock className="text-yellow-400 text-3xl" />
                <div><h3 className="text-yellow-400 font-bold text-lg">Under Review</h3><p className="text-gray-300">Your financial advisor is reviewing your details. You'll receive a notification once they grant you access to view your detailed report.</p></div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Announcements Section - Visible for all users including free */}
      {announcements.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] overflow-hidden" data-testid="announcements-section">
          <div className="p-4 border-b border-[#2D5A8F] flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center"><FaBullhorn className="text-[#FFD700]" /></div>
              <div><h3 className="text-white font-bold">Announcements & Updates</h3><p className="text-gray-400 text-sm">Latest from your financial advisor</p></div>
            </div>
            <span className="px-2 py-1 bg-[#FFD700]/20 text-[#FFD700] text-xs font-bold rounded-full">{announcements.length} NEW</span>
          </div>
          <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
            {announcements.map((announcement) => <AnnouncementCard key={announcement.id} announcement={announcement} />)}
          </div>
        </div>
      )}

      {/* Learning Resources - Visible for all users */}
      <ContentLibrary title="Paiso Ki Pathshala - Learning Resources" isFreePlan={isFreePlan} />

      {/* Refer a Friend Modal */}
      <ReferFriendModal 
        isOpen={showReferModal} 
        onClose={() => setShowReferModal(false)}
        onSuccess={() => setMessage({ type: 'success', text: 'Referral submitted successfully! You\'ll earn ₹300 when your friend becomes a client.' })}
      />

      {/* Upgrade Modal for Free Users */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div>
  );
};

export default ClientDashboard;
